// T-068 — Cassetto menu del cliente: upload + lista + visualizzazione.
//
// Il consulente (Pierpaolo, platform owner) carica qui foto/PDF del
// menù del cliente. Una volta caricato, in chat dice all'agente
// "elabora il menu di <locale>": l'agente legge da Storage il file
// più recente non `processed`, estrae i piatti/ingredienti via AI e
// fa il First Setup degli alimenti in `foods`. Poi marca processed.
//
// Niente UI di "elabora ora" qui: il processing è on-demand,
// guidato dalla chat. Questa pagina è solo il deposito visibile.

import { toUserMessage } from '../../lib/errors.js';
import { confirmDialog } from '../../lib/ui/dialogs.js';
import {
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonPage,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { camera, archiveOutline, trashOutline, checkmarkCircleOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { getCaptureProvider } from '../../lib/adapters/camera/index.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useMasters } from '../../lib/data/queries.js';
import { getSupabase } from '../../lib/supabase.js';
import { compressImageIfNeeded } from '../../lib/imageCompress.js';
import { palette, tileGradients, type TileGradient } from '../../components/design.js';

interface MenuUploadRow {
  id: string;
  tenant_id: string;
  business_id: string;
  file_path: string;
  file_mime: string;
  original_name: string | null;
  note: string | null;
  status: 'pending' | 'processed' | 'archived';
  uploaded_at: string;
  processed_at: string | null;
  processed_summary: Record<string, unknown> | null;
}

interface SignedPreview {
  uploadId: string;
  url: string;
  mime: string;
  expiresAt: number;
}

const STATUS_LABEL: Record<MenuUploadRow['status'], { label: string; color: string }> = {
  pending: { label: '🕓 In attesa', color: 'warning' },
  processed: { label: '✅ Elaborato', color: 'success' },
  archived: { label: '📦 Archiviato', color: 'medium' },
};

export function MenuUploadsPage(): JSX.Element {
  const history = useHistory();
  const { profile } = useAuth();
  const sb = getSupabase();

  const { data: tenants = [] } = useMasters<{ id: string; name: string }>('tenants');
  const { data: businesses = [] } = useMasters<{ id: string; tenant_id: string; name: string }>('businesses');
  const businessId = profile?.active_business_id ?? null;
  const tenantId = profile?.active_tenant_id ?? null;
  const activeTenantName = useMemo(
    () => tenants.find((t) => t.id === tenantId)?.name ?? null,
    [tenants, tenantId],
  );
  const activeBusinessName = useMemo(
    () => businesses.find((b) => b.id === businessId)?.name ?? null,
    [businesses, businessId],
  );

  const [uploads, setUploads] = useState<MenuUploadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState<MenuUploadRow | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [noteEditFor, setNoteEditFor] = useState<MenuUploadRow | null>(null);
  const [noteValue, setNoteValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const signedCache = useRef<Map<string, SignedPreview>>(new Map());

  async function fetchUploads(): Promise<void> {
    if (!businessId || !tenantId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await sb
        .from('menu_uploads')
        .select('id,tenant_id,business_id,file_path,file_mime,original_name,note,status,uploaded_at,processed_at,processed_summary')
        .eq('tenant_id', tenantId)
        .eq('business_id', businessId)
        .is('archived_at', null)
        .order('uploaded_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      setUploads((data ?? []) as MenuUploadRow[]);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchUploads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, tenantId]);

  async function ingestFile(rawB64: string, rawMime: string, filename: string): Promise<void> {
    if (!businessId || !tenantId) {
      setToast('Seleziona prima un locale dalle Impostazioni');
      return;
    }
    setUploading(true);
    try {
      // Compressione coerente con import DDT (T-066).
      const compressed = await compressImageIfNeeded(rawB64, rawMime);
      const ext = compressed.mime === 'application/pdf'
        ? 'pdf'
        : compressed.mime.includes('png')
          ? 'png'
          : 'jpg';
      const id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const path = `${tenantId}/${businessId}/${id}.${ext}`;
      // Upload diretto a Storage. La policy `menus bucket tenant rw`
      // (T-068 migration) permette l'INSERT solo se il primo segmento
      // del path è un tenant del JWT.
      const bytes = Uint8Array.from(atob(compressed.b64), (c) => c.charCodeAt(0));
      const { error: upErr } = await sb.storage
        .from('menus')
        .upload(path, bytes, { contentType: compressed.mime, upsert: false });
      if (upErr) throw upErr;

      const { error: insErr } = await sb.from('menu_uploads').insert({
        tenant_id: tenantId,
        business_id: businessId,
        file_path: path,
        file_mime: compressed.mime,
        original_name: filename,
        status: 'pending',
        uploaded_by: profile?.id ?? null,
      });
      if (insErr) {
        // Best-effort cleanup: se l'INSERT fallisce non lasciamo file orfano.
        await sb.storage.from('menus').remove([path]).catch(() => undefined);
        throw insErr;
      }
      setToast(`Menu caricato (${Math.round(compressed.sizeBytes / 1024)} KB)`);
      await fetchUploads();
    } catch (err) {
      setToast(toUserMessage(err, 'Errore upload menu'));
    } finally {
      setUploading(false);
    }
  }

  async function pick(source: 'camera' | 'gallery'): Promise<void> {
    try {
      const photo = await getCaptureProvider().takePhoto(source);
      if (!photo) return;
      const name = `Foto ${new Date().toLocaleString('it-IT')}`;
      await ingestFile(photo.base64, photo.mime, name);
    } catch (err) {
      setToast(toUserMessage(err, 'Cattura annullata'));
    }
  }

  function handleFileSelect(ev: ChangeEvent<HTMLInputElement>): void {
    const file = ev.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const idx = dataUrl.indexOf(',');
      if (idx < 0) { setToast('File illeggibile'); return; }
      void ingestFile(dataUrl.slice(idx + 1), file.type || 'application/octet-stream', file.name);
    };
    reader.onerror = () => setToast('Errore lettura file');
    reader.readAsDataURL(file);
    ev.target.value = '';
  }

  async function openPreview(row: MenuUploadRow): Promise<void> {
    setPreviewOpen(row);
    const now = Date.now();
    const cached = signedCache.current.get(row.id);
    if (cached && cached.expiresAt > now) {
      setPreviewUrl(cached.url);
      return;
    }
    setPreviewUrl(null);
    try {
      const { data, error } = await sb.storage.from('menus').createSignedUrl(row.file_path, 60 * 15);
      if (error) throw error;
      const url = data?.signedUrl ?? null;
      if (url) {
        signedCache.current.set(row.id, { uploadId: row.id, url, mime: row.file_mime, expiresAt: now + 14 * 60 * 1000 });
        setPreviewUrl(url);
      }
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function markStatus(row: MenuUploadRow, next: MenuUploadRow['status']): Promise<void> {
    try {
      const patch: Record<string, unknown> = { status: next };
      if (next === 'processed') patch.processed_at = new Date().toISOString();
      const { error } = await sb.from('menu_uploads').update(patch).eq('id', row.id);
      if (error) throw error;
      setToast(next === 'processed' ? 'Marcato come elaborato' : next === 'archived' ? 'Archiviato' : 'Riportato in attesa');
      setPreviewOpen(null);
      await fetchUploads();
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function removeUpload(row: MenuUploadRow): Promise<void> {
    if (!(await confirmDialog(`Eliminare definitivamente "${row.original_name ?? row.file_path}"? Il file sarà cancellato dallo Storage.`, { okText: 'Elimina', danger: true }))) return;
    try {
      // Storage prima, poi DB. Se il DB fallisce, ri-caricabile manualmente.
      await sb.storage.from('menus').remove([row.file_path]).catch(() => undefined);
      const { error } = await sb.from('menu_uploads').delete().eq('id', row.id);
      if (error) throw error;
      setToast('Menu eliminato');
      setPreviewOpen(null);
      await fetchUploads();
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function saveNote(): Promise<void> {
    if (!noteEditFor) return;
    try {
      const { error } = await sb.from('menu_uploads')
        .update({ note: noteValue.trim() || null })
        .eq('id', noteEditFor.id);
      if (error) throw error;
      setToast('Nota salvata');
      setNoteEditFor(null);
      setNoteValue('');
      await fetchUploads();
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  const missingLocale = !businessId || !tenantId;
  const isOwner = !!profile?.is_platform_owner;

  // Owner-only gate (UI). La RLS lato server è la difesa primaria; questo
  // è solo per evitare che un cliente passi all'URL diretto e veda una
  // pagina vuota/confusa.
  if (!isOwner) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start"><IonBackButton defaultHref="/settings/menu" /></IonButtons>
            <IonTitle>Cassetto menu</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{
            margin: '20px 0',
            padding: '16px 18px',
            borderRadius: 12,
            background: '#fef2f2',
            borderLeft: '4px solid #dc2626',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
              🔒 Sezione riservata al consulente HACCP
            </div>
            <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.45 }}>
              Il cassetto menu è uno strumento del consulente di
              configurazione iniziale, non del personale del locale. Se
              hai bisogno di aiuto, contatta il tuo consulente di
              riferimento.
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/settings/menu" /></IonButtons>
          <IonTitle>Cassetto menu</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {/* Banner cliente+locale, identico pattern a CapturePhotoPage (T-067) */}
        <div
          style={{
            margin: '0 0 14px',
            padding: '12px 14px',
            borderRadius: 12,
            background: missingLocale ? '#fef2f2' : '#eff6ff',
            borderLeft: `4px solid ${missingLocale ? '#dc2626' : '#0066cc'}`,
            boxShadow: '0 1px 2px rgba(15,23,42,.04)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2 }}>
                Cassetto menu di
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: palette.text, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {!tenantId ? '⚠️ Nessun cliente attivo' : (activeTenantName ?? '— Cliente —')}
              </div>
              <div style={{ fontSize: 13, color: !businessId ? '#b91c1c' : '#475569', marginTop: 2, fontWeight: !businessId ? 700 : 500, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {!businessId ? '⚠️ Locale non selezionato' : `📍 ${activeBusinessName ?? '—'}`}
              </div>
            </div>
            <button
              type="button"
              onClick={() => history.push('/settings')}
              style={{
                padding: '8px 12px', borderRadius: 8, border: 'none',
                background: missingLocale ? '#dc2626' : '#0066cc',
                color: '#fff', fontSize: 12, fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                boxShadow: '0 1px 2px rgba(0,0,0,.12)',
              }}
            >
              Cambia
            </button>
          </div>
        </div>

        <div style={{ fontSize: 13, color: palette.textMuted, marginBottom: 14, lineHeight: 1.45 }}>
          Carica qui foto o PDF del menù del cliente (anche più di uno: pranzo,
          cena, stagionale). Poi, in chat, chiedi all'agente:{' '}
          <i>"elabora il menu di {activeBusinessName ?? '<locale>'}"</i> —
          l'agente legge i file in attesa, estrae piatti e ingredienti e fa
          il First Setup degli alimenti (oppure ricrea il menù pubblico,
          come per "la terrazza di Gio").
        </div>

        {/* Tile carica */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
          {([
            { icon: '📸', label: 'Scatta', sub: 'Fotocamera', gradient: 'blue' as TileGradient, onClick: () => { void pick('camera'); } },
            { icon: '🖼️', label: 'Galleria', sub: 'Da rullino', gradient: 'teal' as TileGradient, onClick: () => { void pick('gallery'); } },
            { icon: '📁', label: 'File', sub: 'PDF / Immagine', gradient: 'rose' as TileGradient, onClick: () => fileInputRef.current?.click() },
          ]).map((t) => (
            <button
              key={t.label}
              type="button"
              onClick={t.onClick}
              disabled={uploading || missingLocale}
              style={{
                padding: '14px 6px', borderRadius: 14, border: 'none',
                background: tileGradients[t.gradient],
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                cursor: (uploading || missingLocale) ? 'not-allowed' : 'pointer',
                opacity: (uploading || missingLocale) ? 0.5 : 1,
                boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
              }}
            >
              <span style={{ fontSize: 26, lineHeight: 1 }}>{t.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: palette.text }}>{t.label}</span>
              <span style={{ fontSize: 10, color: palette.textMuted }}>{t.sub}</span>
            </button>
          ))}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        {uploading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
            <IonSpinner name="dots" /> <span style={{ fontSize: 13 }}>Upload in corso…</span>
          </div>
        )}

        {/* Lista */}
        <IonList inset>
          <IonListHeader>
            <IonLabel>Menu caricati ({uploads.length})</IonLabel>
          </IonListHeader>
          {loading && (
            <IonItem><IonLabel color="medium">Caricamento…</IonLabel></IonItem>
          )}
          {!loading && uploads.length === 0 && (
            <IonItem><IonLabel color="medium">Nessun menu caricato. Usa i pulsanti qui sopra.</IonLabel></IonItem>
          )}
          {uploads.map((u) => (
            <IonItem
              key={u.id}
              button
              detail
              onClick={() => { void openPreview(u); }}
            >
              <IonIcon icon={camera} slot="start" color={u.status === 'processed' ? 'success' : 'primary'} />
              <IonLabel className="ion-text-wrap">
                <h3>{u.original_name ?? '(senza nome)'}</h3>
                <p style={{ fontSize: 12 }}>
                  {new Date(u.uploaded_at).toLocaleString('it-IT')} · {u.file_mime}
                </p>
                {u.note && (
                  <p style={{ fontSize: 12, color: '#6b7280' }}>📝 {u.note}</p>
                )}
                {u.processed_summary && (
                  <p style={{ fontSize: 11, color: '#16a34a' }}>
                    Estratti: {Object.entries(u.processed_summary).map(([k, v]) => `${k}=${String(v)}`).join(' · ')}
                  </p>
                )}
              </IonLabel>
              <IonBadge slot="end" color={STATUS_LABEL[u.status].color}>
                {STATUS_LABEL[u.status].label}
              </IonBadge>
            </IonItem>
          ))}
        </IonList>

        {/* Modal preview + azioni */}
        <IonModal isOpen={!!previewOpen} onDidDismiss={() => { setPreviewOpen(null); setPreviewUrl(null); }}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{previewOpen?.original_name ?? 'Menu'}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => { setPreviewOpen(null); setPreviewUrl(null); }}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            {previewOpen && (
              <>
                {previewUrl ? (
                  previewOpen.file_mime === 'application/pdf' ? (
                    <iframe src={previewUrl} title="Menu PDF" style={{ width: '100%', height: 'calc(100% - 220px)', border: 0 }} />
                  ) : (
                    <div style={{ background: '#000', minHeight: 'calc(100% - 220px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={previewUrl} alt="Menu" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                    </div>
                  )
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><IonSpinner name="dots" /></div>
                )}
                <IonList>
                  <IonItem button onClick={() => { setNoteEditFor(previewOpen); setNoteValue(previewOpen.note ?? ''); }}>
                    <IonLabel>
                      <h3>📝 Note</h3>
                      <p>{previewOpen.note ?? '— nessuna nota —'}</p>
                    </IonLabel>
                  </IonItem>
                  {previewOpen.status !== 'processed' && (
                    <IonItem button onClick={() => { void markStatus(previewOpen, 'processed'); }}>
                      <IonIcon icon={checkmarkCircleOutline} slot="start" color="success" />
                      <IonLabel>Marca come elaborato</IonLabel>
                    </IonItem>
                  )}
                  {previewOpen.status === 'processed' && (
                    <IonItem button onClick={() => { void markStatus(previewOpen, 'pending'); }}>
                      <IonIcon icon={checkmarkCircleOutline} slot="start" color="warning" />
                      <IonLabel>Riporta in attesa</IonLabel>
                    </IonItem>
                  )}
                  <IonItem button onClick={() => { void markStatus(previewOpen, 'archived'); }}>
                    <IonIcon icon={archiveOutline} slot="start" color="medium" />
                    <IonLabel>Archivia</IonLabel>
                  </IonItem>
                  <IonItem button onClick={() => { void removeUpload(previewOpen); }}>
                    <IonIcon icon={trashOutline} slot="start" color="danger" />
                    <IonLabel color="danger">Elimina definitivamente</IonLabel>
                  </IonItem>
                </IonList>
              </>
            )}
          </IonContent>
        </IonModal>

        {/* Modal modifica nota */}
        <IonModal isOpen={!!noteEditFor} onDidDismiss={() => setNoteEditFor(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Nota menu</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setNoteEditFor(null)}>Annulla</IonButton>
                <IonButton strong onClick={() => { void saveNote(); }}>Salva</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonItem>
              <IonInput
                label="Titolo / etichetta"
                labelPlacement="stacked"
                value={noteValue}
                placeholder="es. menù invernale 2026, menù pranzo, aperitivo"
                onIonInput={(ev) => setNoteValue(String(ev.detail.value ?? ''))}
              />
            </IonItem>
            <div style={{ fontSize: 12, color: palette.textMuted, padding: '12px 16px' }}>
              La nota aiuta a distinguere più menu caricati per lo stesso locale
              (es. pranzo vs cena, stagionali, varianti). L'agente la usa come
              hint quando elabora il file.
            </div>
            <IonItem>
              <IonTextarea
                label="Note libere"
                labelPlacement="stacked"
                value={noteValue}
                onIonInput={(ev) => setNoteValue(String(ev.detail.value ?? ''))}
                rows={4}
              />
            </IonItem>
          </IonContent>
        </IonModal>

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={2500}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}
